import { formatAnswer } from '../../shared/answerFormat';
import { isLayoutType } from '../../shared/fieldTypes';

// The entry card's data model (design entry_detail.jsx), kept free of React
// so it can be tested on its own. Every function reads the submission's own
// version definition: labels, options and sections are the ones the person
// saw when they filled the form in.

const fieldsOf = (definition) =>
  definition && Array.isArray(definition.fields)
    ? definition.fields.filter((f) => f && f.id)
    : [];

const lower = (s) => String(s || '').toLowerCase();
const configOf = (field) => (field && field.config) || {};

export const isAnswerField = (field) => !!field && !isLayoutType(field.type);

export const isAgreement = (field) =>
  !!field && field.type === 'checkbox' && configOf(field).single === true;

// Wide answers take a whole row of the grid: long text, an address, the
// grid types and a multi-select checkbox list.
const WIDE_TYPES = ['long_answer', 'address', 'repeater', 'table', 'matrix'];
export const isWide = (field) =>
  !!field &&
  (WIDE_TYPES.includes(field.type) ||
    (field.type === 'checkbox' && !isAgreement(field)));

// Sections in form order. Answer fields before the first section go in a
// "Details" group; sections left with no answer fields are dropped.
export const sectionsOf = (definition) => {
  const out = [];
  let current = null;
  fieldsOf(definition).forEach((field) => {
    if (field.type === 'section') {
      current = {
        id: field.id,
        label: String(field.label || '').trim() || 'Section',
        fields: [],
      };
      out.push(current);
      return;
    }
    if (!isAnswerField(field)) return;
    if (!current) {
      current = { id: '_details', label: 'Details', fields: [] };
      out.push(current);
    }
    current.fields.push(field);
  });
  return out.filter((s) => s.fields.length);
};

// Grid column spans so every row is full: a wide field takes the whole row,
// and the last tile of a short run stretches to close the gap.
export const spans = (fields, n) => {
  const out = [];
  let used = 0;
  let last = -1;
  fields.forEach((field) => {
    if (n === 1 || isWide(field)) {
      if (used && last >= 0) out[last] += n - used;
      out.push(n);
      used = 0;
      last = -1;
      return;
    }
    out.push(1);
    used += 1;
    last = out.length - 1;
    if (used === n) {
      used = 0;
      last = -1;
    }
  });
  if (used && last >= 0) out[last] += n - used;
  return out;
};

// Columns follow the card's own width (Implementation List §1).
export const columnsForWidth = (width) => {
  if (width >= 1080) return 4;
  if (width >= 760) return 3;
  if (width >= 480) return 2;
  return 1;
};

const ICON_BY_TYPE = {
  name: 'user',
  phone: 'phone',
  email: 'mail',
  address: 'map-pin',
  datetime: 'calendar',
  file_upload: 'paperclip',
  rating: 'star',
  repeater: 'users',
  table: 'table',
  matrix: 'grid-3x3',
  number: 'hash',
  dropdown: 'list',
  radio: 'circle-dot',
  long_answer: 'align-left',
};

export const iconFor = (field) => {
  if (!field) return 'type';
  if (field.type === 'checkbox') {
    return isAgreement(field) ? 'shield-check' : 'square-check';
  }
  if (ICON_BY_TYPE[field.type]) return ICON_BY_TYPE[field.type];
  const label = lower(field.label);
  if (/hometown|city|state|zip|address/.test(label)) return 'map-pin';
  if (label.includes('number')) return 'hash';
  return 'type';
};

const formatted = (field, answers) =>
  field ? formatAnswer(field, (answers || {})[field.id]) : '';

// The card's title: a Name field, else a team, else driver first + last,
// else who submitted it, else the entry's reference. A number field whose
// label mentions "car" becomes a muted "#42" after it.
export const heroOf = (definition, answers, entry) => {
  const fields = fieldsOf(definition).filter(isAnswerField);
  const find = (pred) => fields.find(pred);
  const nameField = find((f) => f.type === 'name');
  const teamField = find(
    (f) => f.type === 'short_answer' && lower(f.label).includes('team')
  );
  const first = find((f) => lower(f.label).includes('driver first'));
  const last = find((f) => lower(f.label).includes('driver last'));
  const car = find(
    (f) => f.type === 'number' && lower(f.label).includes('car')
  );
  const driver = [formatted(first, answers), formatted(last, answers)]
    .filter(Boolean)
    .join(' ');
  const title =
    formatted(nameField, answers) ||
    formatted(teamField, answers) ||
    driver ||
    (entry && entry.submitter_name) ||
    (entry && entry.reference) ||
    'Entry';
  const carNumber = formatted(car, answers);
  return { title, suffix: carNumber ? `#${carNumber}` : '' };
};

// The hero's contact chips: from a "contact" section when the form has one
// (not an emergency contact), else the form's first phone and email.
export const contactOf = (definition) => {
  const groups = [{ label: '', fields: [] }];
  fieldsOf(definition).forEach((field) => {
    if (field.type === 'section') {
      groups.push({ label: field.label || '', fields: [] });
    } else if (isAnswerField(field)) {
      groups[groups.length - 1].fields.push(field);
    }
  });
  const all = groups.reduce((acc, g) => acc.concat(g.fields), []);
  const contact = groups.find(
    (g) =>
      /contact/i.test(g.label) &&
      !/emergency/i.test(g.label) &&
      g.fields.some((f) => f.type === 'phone' || f.type === 'email')
  );
  const pool = contact ? contact.fields : all;
  const firstOf = (list, type) => list.find((f) => f.type === type) || null;
  return {
    person: contact
      ? contact.fields.find(
          (f) =>
            f.type === 'name' ||
            (f.type === 'short_answer' && /name/i.test(f.label))
        ) || null
      : null,
    phone: firstOf(pool, 'phone') || firstOf(all, 'phone'),
    email: firstOf(pool, 'email') || firstOf(all, 'email'),
  };
};

// ---- edit mode -----------------------------------------------------------

// A value in the shape the comparison and the patch use: blank strings,
// empty lists and objects, and `false` (the server never stores an unticked
// agreement) all become null; objects lose their blank keys (sorted, so the
// JSON comparison is stable) and lists their blank items. A phone with no
// number is blank whatever its country code.
export const normalizeValue = (value) => {
  if (value == null || value === false) return null;
  if (Array.isArray(value)) {
    const items = value.map(normalizeValue).filter((v) => v !== null);
    return items.length ? items : null;
  }
  if (typeof value === 'object') {
    const out = {};
    Object.keys(value)
      .sort()
      .forEach((key) => {
        const v = normalizeValue(value[key]);
        if (v !== null) out[key] = v;
      });
    return Object.keys(out).length ? out : null;
  }
  if (typeof value === 'string') return value.trim() === '' ? null : value;
  return value;
};

export const normalizeFor = (field, value) => {
  if (field && field.type === 'phone' && value && typeof value === 'object') {
    return String(value.number || '').replace(/\D/g, '')
      ? normalizeValue(value)
      : null;
  }
  return normalizeValue(value);
};

const same = (field, a, b) =>
  JSON.stringify(normalizeFor(field, a)) ===
  JSON.stringify(normalizeFor(field, b));

// Field ids whose draft value differs from the saved answer, in form order.
export const changedKeys = (draft, original, fields) =>
  (fields || [])
    .filter((f) => !same(f, (draft || {})[f.id], (original || {})[f.id]))
    .map((f) => f.id);

// updateFormSubmissionAdmin's partial `answers`: only the changed fields, a
// cleared answer as null.
export const patchOf = (draft, keys, fields) =>
  keys.reduce((acc, id) => {
    const field = (fields || []).find((f) => f.id === id) || null;
    acc[id] = normalizeFor(field, (draft || {})[id]);
    return acc;
  }, {});

export const hasFiles = (field, value) =>
  !!field &&
  field.type === 'file_upload' &&
  !!value &&
  typeof value === 'object' &&
  value.file_id != null;
